import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Box, Alert, CircularProgress } from "@mui/material";
import axios from "axios";
import io from "socket.io-client";
import TrikotGrid from "../components/TrikotGrid";
import BidDialog from "../components/BidDialog";
import SortingFilter from "../components/SortingFilter";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL, SOCKET_URL } from "../config";

const Dashboard = () => {
  const { user } = useAuth();
  const [trikots, setTrikots] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTrikot, setSelectedTrikot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("currentBid");
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const errorTimeoutRef = useRef(null);
  const sortOptionRef = useRef("currentBid");
  const sortDirectionRef = useRef("desc");

  const fetchTrikots = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/trikots`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data && response.data.trikots) {
        setTrikots(response.data.trikots);
        setError(null);
      } else {
        throw new Error("Ungültiges Datenformat vom Server");
      }
    } catch (err) {
      console.error("Error fetching trikots:", err);
      setError(
        "Fehler beim Laden der Trikots: " +
          (err.response?.data?.message ||
            err.message ||
            "Verbindung zum Server nicht möglich")
      );
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    const initSocket = () => {
      if (!socketRef.current) {
        const token = localStorage.getItem("token");
        socketRef.current = io(SOCKET_URL, {
          transports: ["websocket"],
          autoConnect: false,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 500,
          reconnectionDelayMax: 2000,
          timeout: 5000,
          auth: {
            token: token || null,
          },
        });

        socketRef.current.on("connect", () => {
          console.log("Socket connected");
          setError(null);
          // Clear any error timeout
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
          }
          // Always refresh data when reconnecting
          fetchTrikots();
        });

        socketRef.current.on("disconnect", (reason) => {
          console.log("Socket disconnected:", reason);
          
          // Only show error for unexpected disconnects, not for manual disconnects
          if (reason === "io server disconnect" || reason === "io client disconnect") {
            // Manual disconnect, don't show error
            return;
          }
          
          // For other disconnects, wait a bit before showing error
          // This handles temporary network issues on mobile
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }
          
          errorTimeoutRef.current = setTimeout(() => {
            // Only show error if still disconnected after 3 seconds
            if (socketRef.current && !socketRef.current.connected) {
              setError("Verbindung zum Server getrennt - Versuche erneut zu verbinden...");
            }
          }, 3000);
        });

        socketRef.current.on("connect_error", (error) => {
          console.error("Socket connection error:", error);
          
          // Clear any existing timeout
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }
          
          // Only show error after a delay to avoid flickering on mobile
          errorTimeoutRef.current = setTimeout(() => {
            // Check if still not connected
            if (socketRef.current && !socketRef.current.connected) {
              setError(
                "Verbindungsfehler zum Server - Versuche erneut zu verbinden..."
              );
            }
          }, 2000);
        });
        
        socketRef.current.on("reconnect", (attemptNumber) => {
          console.log("Socket reconnected after", attemptNumber, "attempts");
          setError(null);
          // Refresh data after reconnection
          fetchTrikots();
        });
        
        socketRef.current.on("reconnect_attempt", (attemptNumber) => {
          console.log("Reconnection attempt", attemptNumber);
        });
        
        socketRef.current.on("reconnect_error", (error) => {
          console.error("Reconnection error:", error);
        });
        
        socketRef.current.on("reconnect_failed", () => {
          console.error("Reconnection failed");
          setError("Verbindung zum Server konnte nicht wiederhergestellt werden. Bitte Seite neu laden.");
        });

        socketRef.current.on(
          "bidUpdate",
          ({ trikotId, currentBid, highestBidder, endTime }) => {
            setTrikots((prevTrikots) => {
              const newTrikots = prevTrikots.map((trikot) =>
                trikot._id === trikotId
                  ? {
                      ...trikot,
                      currentBid,
                      highestBidder,
                      endTime: endTime || trikot.endTime,
                    }
                  : trikot
              );

              if (sortOptionRef.current === "currentBid") {
                return [...newTrikots].sort((a, b) =>
                  sortDirectionRef.current === "asc"
                    ? a.currentBid - b.currentBid
                    : b.currentBid - a.currentBid
                );
              }
              return newTrikots;
            });
          }
        );

        socketRef.current.on("trikotUpdate", ({ trikotId, updatedTrikot }) => {
          setTrikots((prevTrikots) =>
            prevTrikots.map((trikot) =>
              trikot._id === trikotId ? { ...trikot, ...updatedTrikot } : trikot
            )
          );
        });

        // Connect the socket
        socketRef.current.connect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("Tab became visible, checking socket connection...");
        
        // Check actual socket connection status, not just state
        const socket = socketRef.current;
        if (socket) {
          // Check if socket is actually connected
          if (!socket.connected) {
            console.log("Socket not connected, attempting to reconnect...");
            socket.connect();
          } else {
            console.log("Socket already connected");
            // Even if connected, refresh data to ensure we have latest
            fetchTrikots();
          }
        } else {
          console.log("No socket found, initializing...");
          initSocket();
        }
        
        // Clear any error messages when tab becomes visible
        // They will be set again if connection actually fails
        setError(null);
      } else {
        console.log("Tab became hidden");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    initSocket();

    // Capture ref values for cleanup
    const timer = reconnectTimerRef.current;
    const socket = socketRef.current;

    // Cleanup function
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timer) {
        clearTimeout(timer);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sortDirection, setSortDirection] = useState("desc");

  const getSortedTrikots = () => {
    return [...trikots].sort((a, b) => {
      let comparison = 0;

      switch (sortOption) {
        case "number":
          comparison = parseInt(a.number) - parseInt(b.number);
          break;
        case "currentBid":
          comparison = a.currentBid - b.currentBid;
          break;
        case "endTime":
          comparison = new Date(a.endTime) - new Date(b.endTime);
          break;
        default:
          return 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  };

  const handleSortChange = (newValue) => {
    setSortOption(newValue);
    sortOptionRef.current = newValue;
  };

  const handleDirectionChange = (newDirection) => {
    setSortDirection(newDirection);
    sortDirectionRef.current = newDirection;
  };

  const handleBid = (trikot) => {
    const isExpired = new Date(trikot.endTime) < new Date();
    if (isExpired) {
      setError("Diese Auktion ist bereits beendet.");
      return;
    }
    setSelectedTrikot(trikot);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container maxWidth="xlg">
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              mt: { xs: 8, sm: 8 },
            }}
          >
            {error}
          </Alert>
        )}

        <SortingFilter
          sortOption={sortOption}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onDirectionChange={handleDirectionChange}
        />

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
              mt: { xs: 2, sm: 2 },
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TrikotGrid
            key={sortOption}
            trikots={getSortedTrikots()}
            onBid={handleBid}
            isLoggedIn={Boolean(user)}
          />
        )}

        {selectedTrikot && (
          <BidDialog
            trikot={selectedTrikot}
            open={Boolean(selectedTrikot)}
            onClose={() => setSelectedTrikot(null)}
          />
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
